/* eslint-disable react/prop-types */
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const SERIF = "'DM Sans', sans-serif";

/**
 * Premium centered dialog used to surface the legal policies from the footer
 * (Terms, Privacy, Cancellation & Refund, Shipping) without navigating away.
 * Closes on the Cancel button (top-right), backdrop click, or Escape; locks
 * background scroll while open.
 */
export default function PolicyModal({ open, title, onClose, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* card */}
      <div className="relative z-10 flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-[0_40px_90px_-30px_rgba(8,15,40,0.7)] ring-1 ring-black/10">
        {/* header, premium navy band with a gold accent */}
        <div className="relative shrink-0 overflow-hidden bg-gradient-to-r from-[#0c2655] via-[#102f63] to-[#153568] px-6 py-4">
          <span className="pointer-events-none absolute -right-6 -top-10 h-28 w-28 rounded-full bg-[#1c4b93]/15 blur-2xl" />
          <span className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#f0a500]/60 to-transparent" />
          <div className="relative flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#f0a500]">
                Legal
              </p>
              <h2
                className="mt-0.5 truncate text-lg font-bold text-white sm:text-xl"
                style={{ fontFamily: SERIF }}
              >
                {title}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white/10 px-3.5 py-1.5 text-xs font-semibold text-white ring-1 ring-white/25 backdrop-blur transition hover:bg-white/20 hover:ring-white/40"
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
          </div>
        </div>

        {/* scrollable policy body */}
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  );
}
